/**
 * What the ⋯ menu offers, for one post and one viewer.
 *
 * This is the single place those rules live. Before it, every screen built its
 * own menu inline against the raw dropdown primitive — eight hand-written
 * copies — so the same post showed different options depending on where it was
 * viewed, and each new option had to be remembered in eight places. It usually
 * was not: "Edit a yap for 15 minutes" (build 2222) reached the feed, the
 * detail screen and replies, and never reached shouts.
 *
 * Nothing here renders. It takes facts and returns the list of actions the
 * viewer is entitled to, so the same question asked from any surface gets the
 * same answer, and so the answer can be tested without mounting a screen.
 */

/** Every action any ⋯ menu can offer. */
export type PostMenuAction =
  | 'view-profile'
  | 'share'
  | 'copy-link'
  | 'download'
  | 'see-likes'
  | 'edit'
  | 'delete'
  | 'report'
  | 'block'
  | 'archive'
  | 'remove-shout-extras'
  | 'pin-feed'
  | 'unpin-feed'
  | 'mark-trending'
  | 'unmark-trending'
  | 'hide-from-preview'
  | 'show-in-preview'
  | 'copy-id'
  | 'legion-delete';

/**
 * Where the menu is being rendered.
 *
 * Only used for the handful of actions that are genuinely surface-specific
 * (see SURFACE_ONLY below). Everything else is decided by the post and the
 * viewer alone, which is the point: a surface cannot quietly drop an option by
 * forgetting to ask for it.
 */
export type PostSurface = 'feed' | 'detail' | 'profile' | 'group' | 'reply';

export interface PostMenuPost {
  id: string;
  /** Original author. On a merged shout this is the ORIGINAL yap's author. */
  userId?: string | number | null;
  /** Some screens carry the author under this name instead. */
  authorId?: string | number | null;
  /** The shouter. Present only on shouts, and the real owner of one. */
  shoutedBy?: string | number | null;
  isShout?: boolean;
  type?: string;
  timestamp?: any;
  tribeId?: string | null;
  forumId?: string | null;
  topicId?: string | null;
  visibility?: string | null;
  isAnonymous?: boolean;
  pinnedToFeed?: boolean;
  isTrending?: boolean;
  /** Moderator flag: left out of the logged-out World preview. */
  hiddenFromPreview?: boolean;
  shoutCaption?: string | null;
  shoutImages?: string[] | null;
}

export interface PostMenuViewer {
  id?: string | number | null;
  rank?: string | null;
  /** False for a signed-out web visitor. */
  isAuthed: boolean;
}

/**
 * Privacy of the tribe or forum a post belongs to.
 *
 * The one input a surface must supply, because it lives on the GROUP document
 * and not on the post. Download is the only action that reads it, and its
 * absence is treated as "deny" — a privacy gate that fails open is worse than a
 * missing menu entry.
 */
export interface PostMenuGroup {
  id: string;
  /** University tribes: approval-gated, members only. */
  requiresApproval?: boolean;
  /** Explicitly false means invite-only. */
  isPublic?: boolean;
  /** Whether THIS viewer is an approved member. */
  isMember?: boolean;
}

interface PostMenuInputBase {
  post: PostMenuPost;
  viewer: PostMenuViewer;
  /** Milliseconds left in the edit window; 0 or less means closed. */
  editWindowMs?: number;
  /**
   * The viewer owns the thing this content hangs off — the post a reply is on.
   *
   * Grants Delete but NOT Edit: removing someone's words from your own post is a
   * different power from rewriting them. Used by the reply menu, where the post
   * author has always been able to delete any reply on their post.
   */
  viewerOwnsParent?: boolean;
}

/**
 * The input, discriminated on `surface`.
 *
 * On the 'group' surface `group` is REQUIRED, so a tribe or forum call site
 * cannot compile without telling us how the group is closed. The runtime
 * fail-closed in canDownload stays as a backstop for callers that reach this
 * through an `any` — the type is the guard, not the guarantee.
 */
export type PostMenuInput =
  | (PostMenuInputBase & { surface: 'group'; group: PostMenuGroup })
  | (PostMenuInputBase & {
      surface: Exclude<PostSurface, 'group'>;
      /** A cross-posted group yap can appear off-surface; still honoured. */
      group?: PostMenuGroup | null;
    });

/** Widened shape for the internal helpers, which do not need the narrowing. */
type PostMenuInputLoose = PostMenuInputBase & {
  surface: PostSurface;
  group?: PostMenuGroup | null;
};

/**
 * Actions that exist on exactly one kind of surface.
 *
 * Archive is a profile-shelf action and means nothing in a feed. Removing a
 * shout's caption and images only applies where a shout is the subject.
 * Everything absent from this map is available wherever the viewer is entitled
 * to it.
 */
const SURFACE_ONLY: Partial<Record<PostMenuAction, PostSurface[]>> = {
  archive: ['profile'],
  'remove-shout-extras': ['detail'],
};

/**
 * THE ownership test. Handles shouts correctly, which is the bug that made
 * this module necessary.
 *
 * A merged shout carries the ORIGINAL author under `userId` (the feed item is
 * built by spreading originalYap) and the shouter under `shoutedBy`. The
 * shouter owns the shout. Three different versions of this check existed, each
 * with a different fallback:
 *
 *   YapTabScreen     userId || shoutedBy   → right for shouts by luck of order
 *   YapDetailScreen  userId || authorId    → matched the ORIGINAL author
 *   ShoutDetails     no check at all
 *
 * So the same shout was "mine" in the feed and not mine on its own detail
 * screen. Checking `shoutedBy` FIRST when the post is a shout is what fixes it;
 * the `||` fallbacks never could, because `userId` is always populated.
 */
export function isOwner(post: PostMenuPost, viewerId?: string | number | null): boolean {
  const me = String(viewerId ?? '');
  if (!me) return false;
  if (isShoutPost(post)) return String(post.shoutedBy ?? '') === me;
  return String(post.userId ?? post.authorId ?? '') === me;
}

/** A shout, however the surface happens to label it. */
export function isShoutPost(post: PostMenuPost): boolean {
  return post.isShout === true || post.type === 'shouted' || !!post.shoutedBy;
}

/** True when the post lives in a tribe or forum rather than the world feed. */
export function isGroupPost(post: PostMenuPost): boolean {
  return !!(post.tribeId || post.forumId || post.topicId);
}

const isWatcher = (v: PostMenuViewer) => String(v.rank || '').toLowerCase() === 'watcher';
const isLegion = (v: PostMenuViewer) => String(v.rank || '').toLowerCase() === 'legion';
/** Legion outranks Watcher, so it holds the Watcher tools too. */
const isModerator = (v: PostMenuViewer) => isWatcher(v) || isLegion(v);

/**
 * Whether this post may be saved as an image.
 *
 * World posts follow their own visibility. Group posts additionally depend on
 * how the group is closed:
 *   - invite-only (isPublic === false)  → never, for anyone
 *   - approval-gated (requiresApproval) → approved members, and moderators
 *   - otherwise                         → as visible as the post itself
 *
 * Absent group information denies, rather than assuming a group is open.
 */
function canDownload(input: PostMenuInputLoose): boolean {
  const { post, viewer, group } = input;
  if (post.isAnonymous) return false;
  if (post.visibility && post.visibility !== 'public') return false;

  if (!isGroupPost(post)) return true;

  if (!group) return false;
  if (group.isPublic === false) return false;
  if (group.requiresApproval) return group.isMember === true || isModerator(viewer);
  return true;
}

/**
 * Pinning is a WORLD-feed act.
 *
 * YapTabScreen floats pinnedToFeed posts above the World timeline, so pinning a
 * tribe or forum post would pin it into a feed it can never appear in. Shouts
 * are pinnable, but the pin belongs on the ORIGINAL yap — see pinTargetId.
 */
function canPin(post: PostMenuPost): boolean {
  return !isGroupPost(post) && !post.isAnonymous;
}

/**
 * Which document a pin should be written to.
 *
 * A shout is its own document, but pinnedToFeed is read off yaps, so pinning a
 * shout has to pin the yap it quotes. Callers pass this to pinYapToFeed rather
 * than the id they happen to be rendering.
 */
export function pinTargetId(post: PostMenuPost & { originalYapId?: string | null }): string {
  if (isShoutPost(post) && post.originalYapId) return String(post.originalYapId);
  return String(post.id);
}

/**
 * The actions this viewer may take on this post, in display order.
 *
 * A signed-out visitor gets an empty list: the component turns the menu into a
 * single login prompt rather than showing actions that cannot work.
 */
export function buildPostMenuOptions(input: PostMenuInput): PostMenuAction[] {
  const { post, viewer, surface, editWindowMs = 0, viewerOwnsParent = false } =
    input as PostMenuInputLoose;

  if (!viewer.isAuthed) return [];

  const mine = isOwner(post, viewer.id);
  const anonymous = post.isAnonymous === true;
  const shout = isShoutPost(post);
  const options: PostMenuAction[] = [];

  const add = (action: PostMenuAction) => {
    const allowed = SURFACE_ONLY[action];
    if (allowed && !allowed.includes(surface)) return;
    options.push(action);
  };

  // ── Available on anything the viewer can see ────────────────────────────
  // An anonymous post has no profile to open, and its author must not be
  // reachable from it.
  if (!anonymous) add('view-profile');
  add('share');
  add('copy-link');
  if (canDownload(input)) add('download');

  // ── The author's own post ───────────────────────────────────────────────
  if (mine) {
    add('see-likes');
    if (editWindowMs > 0) add('edit');
    add('archive');
    if (shout) add('remove-shout-extras');
  }

  // Delete is the one author power that extends to the parent's owner.
  if (mine || viewerOwnsParent) add('delete');

  // ── Someone else's post ─────────────────────────────────────────────────
  // Report and Block belong on every piece of content by another person, on
  // every surface. Anonymous posts stay reportable and their author blockable:
  // anonymity is a display choice, not an exemption.
  if (!mine) {
    add('report');
    if (!anonymous) add('block');
  }

  // ── Moderation ──────────────────────────────────────────────────────────
  if (isModerator(viewer)) add('copy-id');
  // The logged-out preview is the World feed, so only world posts can be
  // curated out of it. Watchers get this too: it hides nothing from members.
  if (isModerator(viewer) && !isGroupPost(post)) {
    add(post.hiddenFromPreview ? 'show-in-preview' : 'hide-from-preview');
  }
  if (isLegion(viewer)) {
    add(post.isTrending ? 'unmark-trending' : 'mark-trending');
    if (canPin(post)) add(post.pinnedToFeed ? 'unpin-feed' : 'pin-feed');
    if (!mine) add('legion-delete');
  }

  return options;
}
